import { useState, useEffect } from "react";
import { authAxios } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Brain,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Edit2,
  Check,
  X,
  Trash2,
  History,
  Shield,
  ShieldOff,
  Settings,
  Sparkles,
  RefreshCw,
  Info,
  Plus,
  ChevronLeft,
  ListFilter,
  Clock,
  MoreHorizontal,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

// Category display names and icons
const CATEGORY_CONFIG = {
  BASIC_PROFILE: { name: "Basic Profile", icon: "👤" },
  PERSONAL_IDENTITY: { name: "Personal Identity", icon: "🪪" },
  PHYSICAL_APPEARANCE: { name: "Physical Appearance", icon: "✨" },
  DEMOGRAPHICS_ORIGINS: { name: "Demographics & Origins", icon: "🌍" },
  REL_CONFIG: { name: "Relationship Config", icon: "💑" },
  REL_HISTORY_DYNAMICS: { name: "Relationship History", icon: "📖" },
  LOVE_ATTACHMENT_INTIMACY: { name: "Love & Attachment", icon: "💕" },
  REL_VALUES_BOUNDARIES: { name: "Values & Boundaries", icon: "🛡️" },
  CORE_PERSONALITY: { name: "Core Personality", icon: "🧠" },
  EMOTIONAL_ORIENTATION: { name: "Emotional Orientation", icon: "💫" },
  COMMUNICATION_CONFLICT: { name: "Communication Style", icon: "💬" },
  ATTACHMENT_PACE_SAFETY: { name: "Attachment & Safety", icon: "🤝" },
  BOUNDARIES_SENSITIVITIES: { name: "Boundaries", icon: "⚠️" },
  ENERGY_RHYTHM: { name: "Energy & Rhythm", icon: "⚡" },
  ATTRACTION_DESIRE: { name: "Attraction & Desire", icon: "🔥" },
  WORK_CAREER: { name: "Work & Career", icon: "💼" },
  LIFESTYLE_INTERESTS: { name: "Lifestyle & Interests", icon: "🎯" },
  HABITS_SELFCARE: { name: "Habits & Self-Care", icon: "🧘" },
  TRAVEL_LIFE_HISTORY: { name: "Travel & History", icon: "✈️" },
  SKILLS_CAPACITIES: { name: "Skills & Capacities", icon: "🎨" },
  PREFERENCES_SENSITIVITIES: { name: "Preferences", icon: "❤️" },
  FAMILY_HOME_LIFE: { name: "Family & Home", icon: "🏠" },
  DREAMS_VISION: { name: "Dreams & Vision", icon: "🌟" },
  SOCIAL_DYNAMICS: { name: "Social Dynamics", icon: "👥" },
  VALUES_WORLDVIEW: { name: "Values & Worldview", icon: "🌐" },
  BIO_DIGITAL_PRESENCE: { name: "Bio & Digital", icon: "📱" },
};

// Progress bar component for memory fill percentage
function MemoryProgressBar({ label, filled, total, color, icon: Icon }) {
  const percentage = total > 0 ? Math.round((filled / total) * 100) : 0;
  
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <span className="text-white/80 text-sm font-medium">{label}</span>
        </div>
        <span className="text-white/60 text-sm">
          {filled} of {total} fields ({percentage}%)
        </span>
      </div>
      <Progress value={percentage} className="h-2" />
    </div>
  );
}

// Field value display component
function FieldValue({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-white/30 italic">Not set</span>;
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-white/30 italic">Empty</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {value.slice(0, 5).map((item, idx) => (
          <Badge key={idx} variant="secondary" className="bg-white/10 text-white/80 text-xs">
            {item}
          </Badge>
        ))}
        {value.length > 5 && (
          <Badge variant="secondary" className="bg-white/10 text-white/50 text-xs">
            +{value.length - 5} more
          </Badge>
        )}
      </div>
    );
  }
  
  const strValue = String(value);
  return (
    <span className="text-white/80">
      {strValue.length > 60 ? `${strValue.substring(0, 60)}...` : strValue}
    </span>
  );
}

// Memory row component (used in both summary and full view)
function MemoryRow({ 
  fieldKey, 
  value, 
  spec,
  source,
  timestamp,
  onEdit, 
  onClear,
  onViewHistory,
  isEditing,
  editValue,
  setEditValue,
  onSaveEdit,
  onCancelEdit,
  compact = false,
  showFieldNames = false,
  userTimezone = "UTC"
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isListField = spec?.value_type === "list_str";
  const hasValue = value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true);
  
  const formatFieldName = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const formatTime = (ts) => {
    if (!ts) return "";
    
    try {
      // Parse the timestamp and convert to user's timezone
      const date = new Date(ts);
      const now = new Date();
      
      // Calculate diff in user's timezone context
      const userNow = new Date(now.toLocaleString("en-US", { timeZone: userTimezone }));
      const userDate = new Date(date.toLocaleString("en-US", { timeZone: userTimezone }));
      const diff = userNow - userDate;
      
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      
      // For older dates, show formatted date in user's timezone
      return date.toLocaleDateString("en-US", { timeZone: userTimezone });
    } catch (e) {
      // Fallback to original logic
      const date = new Date(ts);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return date.toLocaleDateString();
    }
  };

  return (
    <div className={`group py-3 px-3 rounded-lg hover:bg-white/5 transition-colors ${compact ? 'py-2' : ''}`}>
      {/* Main content row */}
      <div className="flex items-start gap-3">
        {/* Field info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white/90 text-sm font-medium">
              {formatFieldName(fieldKey)}
            </span>
            {showFieldNames && (
              <span className="text-cyan-400 font-mono bg-cyan-400/10 px-1.5 py-0.5 rounded text-[10px]">
                knows_{fieldKey}
              </span>
            )}
            {isListField && (
              <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-400">
                list
              </Badge>
            )}
            {spec?.prompt_priority === "high" && (
              <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">
                priority
              </Badge>
            )}
            {source && (
              <Badge variant="outline" className={`text-[10px] ${source === 'live' ? 'border-green-500/30 text-green-400' : 'border-blue-500/30 text-blue-400'}`}>
                {source}
              </Badge>
            )}
          </div>
          
          {/* Timestamp row */}
          {timestamp && (
            <div className="text-white/40 text-xs flex items-center gap-1 mt-1">
              <Clock className="w-3 h-3" />
              {formatTime(timestamp)}
            </div>
          )}
          
          {/* Value display or edit */}
          <div className="mt-1">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="h-8 text-sm bg-white/10 border-white/20"
                  placeholder={isListField ? "Enter values separated by commas" : "Enter value"}
                  autoFocus
                />
                <Button size="sm" variant="ghost" onClick={onSaveEdit} className="h-8 w-8 p-0 text-green-400">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={onCancelEdit} className="h-8 w-8 p-0 text-red-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <FieldValue value={value} />
            )}
          </div>
        </div>
        
        {/* Desktop Controls - hover only */}
        {!isEditing && !confirmDelete && (
          <div className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={onViewHistory}
              className="h-7 w-7 p-0 text-white/50 hover:text-purple-400"
              title="Edit memories"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
            
            {hasValue && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                className="h-7 w-7 p-0 text-white/50 hover:text-red-400"
                title="Clear all"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
        
        {/* Desktop Delete Confirmation */}
        {!isEditing && confirmDelete && (
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <span className="text-white/60 text-xs">Delete?</span>
            <Button
              size="sm"
              onClick={() => { onClear(); setConfirmDelete(false); }}
              className="h-7 bg-red-600 hover:bg-red-700 text-white text-xs px-2"
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              className="h-7 text-white/60 hover:text-white text-xs px-2"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
      
      {/* Mobile Controls - always visible, below value, right-aligned */}
      {!isEditing && (
        <div className="md:hidden mt-2 pt-2 border-t border-white/5">
          {confirmDelete ? (
            /* Mobile delete confirmation */
            <div className="flex items-center justify-end gap-2">
              <span className="text-white/60 text-xs">Delete this memory?</span>
              <Button
                size="sm"
                onClick={() => { onClear(); setConfirmDelete(false); }}
                className="h-7 bg-red-600 hover:bg-red-700 text-white text-xs px-2"
              >
                Confirm
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(false)}
                className="h-7 text-white/60 hover:text-white text-xs px-2"
              >
                Cancel
              </Button>
            </div>
          ) : (
            /* Mobile action buttons */
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={onViewHistory}
                className="h-8 w-8 p-0 text-white/50 hover:text-purple-400"
                title="Edit memories"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              
              {hasValue && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmDelete(true)}
                  className="h-8 w-8 p-0 text-white/50 hover:text-red-400"
                  title="Clear all"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Single field row for full view with controls
function FieldRow({ 
  fieldKey, 
  spec, 
  simValue, 
  realValue, 
  controls,
  onUpdate, 
  onClear,
  onToggleControl,
  onViewClaims,
  activeView,
  showFieldNames = false,
  // User Base Data props
  userPersonaValue = null,
  isUserDataEnabled = true,
  onToggleUserData = null,
  showUserData = false
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const isListField = spec?.value_type === "list_str";
  const value = activeView === "real" ? realValue : simValue;
  const hasValue = value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true);
  
  // Get resolved controls
  const writeEnabled = controls?.write_enabled !== false;
  const useEnabled = controls?.use_enabled !== false;
  
  const handleStartEdit = () => {
    if (Array.isArray(value)) {
      setEditValue(value.join(", "));
    } else {
      setEditValue(value || "");
    }
    setIsEditing(true);
  };
  
  const handleSave = async () => {
    await onUpdate(fieldKey, editValue, isListField ? "append" : "set");
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setEditValue("");
  };
  
  const formatFieldName = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="group flex items-start gap-3 py-3 px-3 rounded-lg hover:bg-white/5 transition-colors">
      {/* Field info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white/90 text-sm font-medium">
            {formatFieldName(fieldKey)}
          </span>
          {showFieldNames && (
            <span className="text-cyan-400 font-mono bg-cyan-400/10 px-1.5 py-0.5 rounded text-[10px]">
              knows_{fieldKey}
            </span>
          )}
          {isListField && (
            <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-400">
              list
            </Badge>
          )}
          {spec?.prompt_priority === "high" && (
            <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">
              priority
            </Badge>
          )}
        </div>
        
        {/* Value display or edit */}
        <div className="mt-1">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="h-8 text-sm bg-white/10 border-white/20"
                placeholder={isListField ? "Enter values separated by commas" : "Enter value"}
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={handleSave} className="h-8 w-8 p-0 text-green-400">
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel} className="h-8 w-8 p-0 text-red-400">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <FieldValue value={value} />
          )}
        </div>
        
        {/* User Persona subscript line - shown when profile sharing is enabled */}
        {showUserData && userPersonaValue && (
          <div className={`mt-1.5 flex items-center gap-2 ${isUserDataEnabled ? 'opacity-100' : 'opacity-50'}`}>
            <Switch
              checked={isUserDataEnabled}
              onCheckedChange={(checked) => onToggleUserData?.(fieldKey, checked)}
              className="scale-[0.65]"
              data-testid={`user-data-toggle-${fieldKey}`}
            />
            <span className={`text-xs font-mono ${isUserDataEnabled ? 'text-amber-400' : 'text-white/40'}`}>
              {isUserDataEnabled ? '+' : '-'}{'{'}user_{fieldKey}{'}'}
            </span>
            <span className={`text-xs ${isUserDataEnabled ? 'text-amber-300/80' : 'text-white/30'} truncate max-w-[220px]`}>
              {userPersonaValue}
            </span>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Edit button */}
        {!isEditing && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewClaims(fieldKey)}
            className="h-7 w-7 p-0 text-white/50 hover:text-purple-400"
            title="Edit memories"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </Button>
        )}
        
        {/* Clear button */}
        {hasValue && !isEditing && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onClear(fieldKey)}
            className="h-7 w-7 p-0 text-white/50 hover:text-red-400"
            title="Clear all"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
        
        {/* Record toggle - Brain icon */}
        <div className="flex items-center gap-1" title={writeEnabled ? "Recording enabled" : "Recording disabled"}>
          <Brain className={`w-3.5 h-3.5 ${writeEnabled ? 'text-purple-400' : 'text-white/30'}`} />
          <Switch
            checked={writeEnabled}
            onCheckedChange={(checked) => onToggleControl(fieldKey, "write", checked)}
            className="scale-75"
          />
        </div>
        
        {/* Use toggle */}
        <div className="flex items-center gap-1" title={useEnabled ? "Used in prompts" : "Not used in prompts"}>
          {useEnabled ? (
            <Eye className="w-3.5 h-3.5 text-blue-400" />
          ) : (
            <EyeOff className="w-3.5 h-3.5 text-white/30" />
          )}
          <Switch
            checked={useEnabled}
            onCheckedChange={(checked) => onToggleControl(fieldKey, "use", checked)}
            className="scale-75"
          />
        </div>
      </div>
    </div>
  );
}

// Category section component for full view
function CategorySection({
  categoryKey,
  fields,
  specs,
  simView,
  realView,
  controls,
  onUpdate,
  onClear,
  onToggleControl,
  onViewClaims,
  activeView,
  showFieldNames = false,
  // User Base Data props
  userBaseDataValues = {},
  isUserDataFieldEnabled = () => true,
  onToggleUserData = null,
  showUserData = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const config = CATEGORY_CONFIG[categoryKey] || { name: categoryKey, icon: "📁" };
  
  // Count fields with values in current view
  const view = activeView === "real" ? realView : simView;
  const filledCount = fields.filter(f => {
    const val = view[f];
    return val !== null && val !== undefined && (Array.isArray(val) ? val.length > 0 : true);
  }).length;
  
  // Get category-level controls if they exist
  const categoryControls = controls?.categories?.[categoryKey];
  const categoryWriteEnabled = categoryControls?.write_enabled !== false;
  const categoryUseEnabled = categoryControls?.use_enabled !== false;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div 
          className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg">{config.icon}</span>
            <span className="text-white font-medium">{config.name}</span>
            <Badge variant="secondary" className={`bg-white/10 ${filledCount > 0 ? 'text-green-400' : 'text-white/60'}`}>
              {filledCount}/{fields.length}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {/* Category-level toggles */}
            <div 
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Brain className={`w-3.5 h-3.5 ${categoryWriteEnabled ? 'text-purple-400' : 'text-white/30'}`} />
              <Switch
                checked={categoryWriteEnabled}
                onCheckedChange={(checked) => onToggleControl(null, "write", checked, categoryKey)}
                className="scale-75"
              />
            </div>
            <div 
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Eye className={`w-3.5 h-3.5 ${categoryUseEnabled ? 'text-blue-400' : 'text-white/30'}`} />
              <Switch
                checked={categoryUseEnabled}
                onCheckedChange={(checked) => onToggleControl(null, "use", checked, categoryKey)}
                className="scale-75"
              />
            </div>
            {isOpen ? (
              <ChevronDown className="w-4 h-4 text-white/50" />
            ) : (
              <ChevronRight className="w-4 h-4 text-white/50" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 space-y-0.5 ml-4 border-l border-white/10 pl-4">
          {fields.map(fieldKey => {
            const spec = specs.find(s => s.field_key === fieldKey);
            const fieldControls = controls?.fields?.[fieldKey];
            
            return (
              <FieldRow
                key={fieldKey}
                fieldKey={fieldKey}
                spec={spec}
                simValue={simView[fieldKey]}
                realValue={realView[fieldKey]}
                controls={fieldControls || categoryControls || controls?.global_flags}
                onUpdate={onUpdate}
                onClear={onClear}
                onToggleControl={onToggleControl}
                onViewClaims={onViewClaims}
                activeView={activeView}
                showFieldNames={showFieldNames}
                userPersonaValue={userBaseDataValues[fieldKey]}
                isUserDataEnabled={isUserDataFieldEnabled(fieldKey)}
                onToggleUserData={onToggleUserData}
                showUserData={showUserData && !!userBaseDataValues[fieldKey]}
              />
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Claims Editor Sheet - View, Edit, Delete individual memory claims
function ClaimsEditorSheet({ matchId, fieldKey, isOpen, onClose, onClaimsChanged }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingClaimId, setEditingClaimId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deletingClaimId, setDeletingClaimId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // Confirm delete state
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [addingLoading, setAddingLoading] = useState(false);
  
  const fetchClaims = async (showErrorToast = true) => {
    setLoading(true);
    try {
      const response = await authAxios.get(`/familiarity-v2/${matchId}/claims/${encodeURIComponent(fieldKey)}`);
      setClaims(response.data.claims || []);
    } catch (error) {
      console.error("Error fetching claims:", error);
      if (showErrorToast) {
        toast.error("Failed to load memories");
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (isOpen && fieldKey && matchId) {
      // Reset all state when opening a different field
      setClaims([]);
      setEditingClaimId(null);
      setEditValue("");
      setIsAdding(false);
      setNewValue("");
      fetchClaims();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fieldKey, matchId]);
  
  const formatFieldName = (key) => {
    return key?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "";
  };
  
  const handleStartEdit = (claim) => {
    setEditingClaimId(claim.claim_id);
    setEditValue(Array.isArray(claim.value) ? claim.value.join(", ") : String(claim.value || ""));
  };
  
  const handleSaveEdit = async (claimId) => {
    try {
      await authAxios.put(`/familiarity-v2/${matchId}/claims/${claimId}`, { value: editValue });
      toast.success("Memory updated");
      setEditingClaimId(null);
      setEditValue("");
      await fetchClaims(false);  // Don't show error toast after successful save
      onClaimsChanged?.();
    } catch (error) {
      console.error("Error updating claim:", error);
      toast.error("Failed to update memory");
    }
  };
  
  const handleCancelEdit = () => {
    setEditingClaimId(null);
    setEditValue("");
  };
  
  const handleDelete = async (claimId) => {
    // If not confirmed yet, show confirmation
    if (confirmDeleteId !== claimId) {
      setConfirmDeleteId(claimId);
      return;
    }
    
    // Confirmed - proceed with delete
    setDeletingClaimId(claimId);
    setConfirmDeleteId(null);
    try {
      await authAxios.delete(`/familiarity-v2/${matchId}/claims/${claimId}`);
      toast.success("Memory deleted");
      await fetchClaims(false);  // Don't show error toast after successful delete
      onClaimsChanged?.();
    } catch (error) {
      console.error("Error deleting claim:", error);
      toast.error("Failed to delete memory");
    } finally {
      setDeletingClaimId(null);
    }
  };
  
  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };
  
  const handleAddMemory = async () => {
    if (!newValue.trim()) {
      toast.error("Please enter a value");
      return;
    }
    
    setAddingLoading(true);
    try {
      await authAxios.post(`/familiarity-v2/${matchId}/claims`, {
        field_key: fieldKey,
        value: newValue.trim()
      });
      toast.success("Memory added");
      setIsAdding(false);
      setNewValue("");
      await fetchClaims(false);  // Don't show error toast after successful add
      onClaimsChanged?.();
    } catch (error) {
      console.error("Error adding claim:", error);
      toast.error("Failed to add memory");
    } finally {
      setAddingLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="bg-zinc-900 border-white/10 w-[450px] sm:max-w-[450px]">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-purple-400" />
            Edit Memories
          </SheetTitle>
          <p className="text-white/60 text-sm">
            {formatFieldName(fieldKey)}
          </p>
        </SheetHeader>
        
        <div className="mt-6 space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto pr-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 text-white/50 animate-spin" />
            </div>
          ) : claims.length === 0 && !isAdding ? (
            <div className="text-center py-8 text-white/40">
              <History className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No memories recorded</p>
              <p className="text-xs mt-1">Click "Add Memory" to create one</p>
            </div>
          ) : (
            claims.map((claim) => (
              <div key={claim.claim_id} className="p-3 bg-white/5 rounded-lg space-y-2 group hover:bg-white/8 transition-colors">
                {/* Header row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${
                      claim.source_mode === "real" ? "border-green-500/30 text-green-400" :
                      claim.source_mode === "user_edit" ? "border-purple-500/30 text-purple-400" :
                      "border-blue-500/30 text-blue-400"
                    }`}>
                      {claim.source_mode || "extracted"}
                    </Badge>
                    <span className="text-white/40 text-xs">
                      {Math.round((claim.confidence || 0) * 100)}% confidence
                    </span>
                  </div>
                  <span className="text-white/40 text-xs">
                    {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
                
                {/* Value display/edit */}
                {editingClaimId === claim.claim_id ? (
                  <div className="space-y-2">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="bg-white/10 border-white/20 text-white text-sm"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleSaveEdit(claim.claim_id)}
                        className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                      >
                        <Check className="w-3 h-3 mr-1" /> Save
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={handleCancelEdit}
                        className="text-white/60 hover:text-white h-7 text-xs"
                      >
                        <X className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {/* Value row */}
                    <div className="text-white/80 text-sm break-words">
                      {Array.isArray(claim.value) ? claim.value.join(", ") : String(claim.value || "")}
                    </div>
                    
                    {/* Delete confirmation row - shows when confirmDeleteId matches */}
                    {confirmDeleteId === claim.claim_id ? (
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                        <span className="text-white/60 text-xs mr-2">Delete this memory?</span>
                        <Button
                          size="sm"
                          onClick={() => handleDelete(claim.claim_id)}
                          disabled={deletingClaimId === claim.claim_id}
                          className="h-7 bg-red-600 hover:bg-red-700 text-white text-xs"
                        >
                          {deletingClaimId === claim.claim_id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            "Confirm"
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelDelete}
                          className="h-7 text-white/60 hover:text-white text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      /* Action buttons row - Desktop: hover, Mobile: always visible */
                      <div className="flex items-center justify-end gap-1 pt-2 border-t border-white/5 md:border-0 md:pt-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStartEdit(claim)}
                          className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                          title="Edit"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(claim.claim_id)}
                          disabled={deletingClaimId === claim.claim_id}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          title="Delete"
                        >
                          {deletingClaimId === claim.claim_id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Evidence excerpt if available */}
                {claim.evidence?.excerpt && (
                  <div className="text-white/40 text-xs italic border-l-2 border-white/20 pl-2 mt-2">
                    &ldquo;{claim.evidence.excerpt}&rdquo;
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Add Memory Section */}
        <div className="mt-4 pt-4 border-t border-white/10">
          {isAdding ? (
            <div className="space-y-3">
              <Input
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Enter memory value..."
                className="bg-white/10 border-white/20 text-white text-sm"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleAddMemory()}
              />
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={handleAddMemory}
                  disabled={addingLoading}
                  className="bg-green-600 hover:bg-green-700 text-white h-8"
                >
                  {addingLoading ? (
                    <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Plus className="w-3.5 h-3.5 mr-1" />
                  )}
                  Add
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => { setIsAdding(false); setNewValue(""); }}
                  className="text-white/60 hover:text-white h-8"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="w-full border-white/20 text-white/70 hover:text-white hover:bg-white/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Memory
            </Button>
          )}
          
          <p className="text-white/40 text-xs text-center mt-3">
            {claims.length} memor{claims.length === 1 ? "y" : "ies"}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Main component
export default function FamiliarityV2({ matchId, matchName, onBack, showFieldNames = false, userTimezone = "UTC" }) {
  const [loading, setLoading] = useState(true);
  const [memory, setMemory] = useState({
    sim_view: {},
    real_view: {},  // Changed from live_view
    controls: { global_flags: { write_enabled: true, use_enabled: true }, categories: {}, fields: {} }
  });
  const [specs, setSpecs] = useState([]);
  const [activeView, setActiveView] = useState("sim");
  const [evidenceField, setEvidenceField] = useState(null);
  
  // User persona name for heading
  const [userPersonaName, setUserPersonaName] = useState("You");
  
  // View mode: "summary" or "manage"
  const [viewMode, setViewMode] = useState("summary");
  
  // Privacy controls help modal
  const [showPrivacyHelp, setShowPrivacyHelp] = useState(false);
  
  // Recent claims for summary view with pagination
  const [recentClaims, setRecentClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsPage, setClaimsPage] = useState(0);
  const [hasMoreClaims, setHasMoreClaims] = useState(true);
  const [totalClaims, setTotalClaims] = useState(0);
  
  // Editing state for summary view
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  
  // User Base Data (Profile Sharing) state
  const [userBaseData, setUserBaseData] = useState({
    sim_master_enabled: true,
    real_master_enabled: true,  // Changed from live_master_enabled
    fields: {},        // Per-field toggles: { field_key: { sim: bool, real: bool } }
    field_values: {},  // User persona values by field_key
    user_name: "You",
    has_user_persona: false
  });
  const [userBaseDataLoading, setUserBaseDataLoading] = useState(false);
  
  // Group specs by category
  const groupedFields = specs.reduce((acc, spec) => {
    const cat = spec.category_key || "OTHER";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(spec.field_key);
    return acc;
  }, {});
  
  // Ordered categories
  const orderedCategories = Object.keys(CATEGORY_CONFIG).filter(cat => groupedFields[cat]?.length > 0);
  
  // Calculate filled fields count
  const countFilledFields = (view) => {
    return Object.values(view).filter(v => 
      v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
    ).length;
  };
  
  const simFilledCount = countFilledFields(memory.sim_view);
  const realFilledCount = countFilledFields(memory.real_view);  // Changed from live
  const totalFields = specs.length || 95;

  // Fetch memory data
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const response = await authAxios.get(`/familiarity-v2/${matchId}`);
        if (isMounted) {
          setMemory({
            sim_view: response.data.sim_view || {},
            real_view: response.data.real_view || {},  // Changed from live_view
            controls: response.data.controls || { global_flags: { write_enabled: true, use_enabled: true }, categories: {}, fields: {} }
          });
          setSpecs(response.data.field_specs || []);
          // Set user persona name for heading
          if (response.data.user_persona_name) {
            setUserPersonaName(response.data.user_persona_name);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching memory:", error);
        if (isMounted) {
          toast.error(error.response?.data?.detail || "Failed to load memory data");
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [matchId]);
  
  // Fetch recent claims for summary - filtered by active view (sim/real)
  useEffect(() => {
    if (matchId && viewMode === "summary") {
      const fetchClaims = async () => {
        setClaimsLoading(true);
        try {
          const response = await authAxios.get(`/familiarity-v2/${matchId}/claims?limit=10&skip=0&mode=${activeView}`);
          const newClaims = response.data.claims || [];
          setRecentClaims(newClaims);
          setHasMoreClaims(response.data.has_more || false);
          setTotalClaims(response.data.total || 0);
          setClaimsPage(0);
        } catch (error) {
          console.error("Error fetching claims:", error);
        } finally {
          setClaimsLoading(false);
        }
      };
      fetchClaims();
    }
  }, [matchId, viewMode, activeView]);  // Added activeView dependency
  
  // Fetch user base data (profile sharing) when entering manage mode
  useEffect(() => {
    if (matchId && viewMode === "manage") {
      const fetchUserBaseData = async () => {
        setUserBaseDataLoading(true);
        try {
          const response = await authAxios.get(`/familiarity-v2/${matchId}/user-base-data`);
          setUserBaseData({
            sim_master_enabled: response.data.sim_master_enabled ?? true,
            real_master_enabled: response.data.real_master_enabled ?? true,
            fields: response.data.fields || {},
            field_values: response.data.field_values || {},
            user_name: response.data.user_name || "You",
            has_user_persona: response.data.has_user_persona || false
          });
        } catch (error) {
          console.error("Error fetching user base data:", error);
        } finally {
          setUserBaseDataLoading(false);
        }
      };
      fetchUserBaseData();
    }
  }, [matchId, viewMode]);
  
  const loadRecentClaims = async (page = 0, reset = false) => {
    setClaimsLoading(true);
    try {
      const skip = page * 10;
      const response = await authAxios.get(`/familiarity-v2/${matchId}/claims?limit=10&skip=${skip}&mode=${activeView}`);
      const newClaims = response.data.claims || [];
      
      if (reset) {
        setRecentClaims(newClaims);
      } else {
        setRecentClaims(prev => [...prev, ...newClaims]);
      }
      
      setHasMoreClaims(response.data.has_more || false);
      setTotalClaims(response.data.total || 0);
      setClaimsPage(page);
    } catch (error) {
      console.error("Error fetching claims:", error);
    } finally {
      setClaimsLoading(false);
    }
  };
  
  const loadMoreClaims = () => {
    loadRecentClaims(claimsPage + 1, false);
  };
  
  // Refresh function
  const refreshMemory = async () => {
    try {
      const response = await authAxios.get(`/familiarity-v2/${matchId}`);
      setMemory({
        sim_view: response.data.sim_view || {},
        real_view: response.data.real_view || {},
        controls: response.data.controls || { global_flags: { write_enabled: true, use_enabled: true }, categories: {}, fields: {} }
      });
      setSpecs(response.data.field_specs || []);
      
      // Also refresh claims
      loadRecentClaims(0, true);
    } catch (error) {
      console.error("Error refreshing memory:", error);
      toast.error("Failed to refresh");
    }
  };

  // Update a field value
  const handleUpdate = async (fieldKey, value, op = "set") => {
    try {
      const response = await authAxios.patch(`/familiarity-v2/${matchId}/field`, {
        field_key: fieldKey,
        value: value,
        op: op,
        mode: activeView
      });
      
      if (response.data.success) {
        setMemory(prev => ({
          ...prev,
          [activeView === "sim" ? "sim_view" : "real_view"]: {
            ...prev[activeView === "sim" ? "sim_view" : "real_view"],
            [fieldKey]: response.data.new_value
          }
        }));
        toast.success("Updated");
        
        // Refresh claims to show new entry
        loadRecentClaims(0, true);
      }
    } catch (error) {
      console.error("Error updating field:", error);
      toast.error("Failed to update");
    }
  };
  
  // Clear a field
  const handleClear = async (fieldKey) => {
    try {
      await authAxios.delete(`/familiarity-v2/${matchId}/field/${fieldKey}?mode=${activeView}`);
      
      setMemory(prev => ({
        ...prev,
        [activeView === "sim" ? "sim_view" : "real_view"]: {
          ...prev[activeView === "sim" ? "sim_view" : "real_view"],
          [fieldKey]: null
        }
      }));
      toast.success("Cleared");
      
      // Refresh claims
      loadRecentClaims(0, true);
    } catch (error) {
      console.error("Error clearing field:", error);
      toast.error("Failed to clear");
    }
  };
  
  // Toggle control
  const handleToggleControl = async (fieldKey, controlType, enabled, categoryKey = null) => {
    try {
      let scope, key;
      
      if (fieldKey) {
        scope = "field";
        key = fieldKey;
      } else if (categoryKey) {
        scope = "category";
        key = categoryKey;
      } else {
        scope = "global";
        key = null;
      }
      
      const payload = {
        scope: scope,
        key: key,
        [controlType === "write" ? "write_enabled" : "use_enabled"]: enabled
      };
      
      const response = await authAxios.patch(`/familiarity-v2/${matchId}/controls`, payload);
      
      if (response.data.success) {
        setMemory(prev => ({
          ...prev,
          controls: response.data.controls
        }));
      }
    } catch (error) {
      console.error("Error toggling control:", error);
      toast.error("Failed to update control");
    }
  };
  
  // Apply preset
  const handleApplyPreset = async (preset) => {
    try {
      await authAxios.post(`/familiarity-v2/${matchId}/controls/preset?preset=${preset}`);
      await refreshMemory();
      toast.success(`Applied "${preset}" preset`);
    } catch (error) {
      console.error("Error applying preset:", error);
      toast.error("Failed to apply preset");
    }
  };
  
  // Summary view edit handlers
  const handleStartEditSummary = (fieldKey, currentValue) => {
    if (Array.isArray(currentValue)) {
      setEditValue(currentValue.join(", "));
    } else {
      setEditValue(currentValue || "");
    }
    setEditingField(fieldKey);
  };
  
  const handleSaveEditSummary = async () => {
    if (!editingField) return;
    const spec = specs.find(s => s.field_key === editingField);
    const isListField = spec?.value_type === "list_str";
    await handleUpdate(editingField, editValue, isListField ? "append" : "set");
    setEditingField(null);
    setEditValue("");
  };
  
  const handleCancelEditSummary = () => {
    setEditingField(null);
    setEditValue("");
  };
  
  // User Base Data (Profile Sharing) handlers
  const handleProfileSharingMasterToggle = async (enabled) => {
    const mode = activeView; // "sim" or "real"
    try {
      const payload = mode === "sim" 
        ? { sim_master_enabled: enabled }
        : { real_master_enabled: enabled };
      
      await authAxios.patch(`/familiarity-v2/${matchId}/user-base-data`, payload);
      
      setUserBaseData(prev => ({
        ...prev,
        [mode === "sim" ? "sim_master_enabled" : "real_master_enabled"]: enabled
      }));
      
      toast.success(`Profile sharing ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("Error updating profile sharing master toggle:", error);
      toast.error("Failed to update");
    }
  };
  
  const handleProfileSharingFieldToggle = async (fieldKey, enabled) => {
    const mode = activeView; // "sim" or "real"
    try {
      const currentField = userBaseData.fields[fieldKey] || { sim: true, live: true };
      
      const payload = {
        field_key: fieldKey,
        field_sim_enabled: mode === "sim" ? enabled : currentField.sim,
        field_real_enabled: mode === "real" ? enabled : currentField.real,
      };
      
      await authAxios.patch(`/familiarity-v2/${matchId}/user-base-data`, payload);
      
      setUserBaseData(prev => ({
        ...prev,
        fields: {
          ...prev.fields,
          [fieldKey]: {
            sim: mode === "sim" ? enabled : (prev.fields[fieldKey]?.sim ?? true),
            real: mode === "real" ? enabled : (prev.fields[fieldKey]?.real ?? true),
          }
        }
      }));
    } catch (error) {
      console.error("Error updating profile sharing field toggle:", error);
      toast.error("Failed to update");
    }
  };
  
  // Check if a user base data field is enabled for current mode
  const isUserBaseDataFieldEnabled = (fieldKey) => {
    const mode = activeView;
    const masterEnabled = mode === "sim" ? userBaseData.sim_master_enabled : userBaseData.real_master_enabled;
    if (!masterEnabled) return false;
    
    const fieldSetting = userBaseData.fields[fieldKey];
    if (fieldSetting) {
      return mode === "sim" ? fieldSetting.sim : fieldSetting.live;
    }
    return true; // Default to enabled
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 text-white/50 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="familiarity-v2">
      {/* Section Heading */}
      <div className="text-center">
        <h2 className="text-lg font-semibold text-divine-gold font-cinzel">
          {matchName}&apos;s memories of {userPersonaName}
        </h2>
      </div>
      
      {/* Simulated / Real Toggle - Large and Prominent */}
      <div className="flex justify-center gap-2 p-1 bg-white/5 rounded-lg max-w-md mx-auto">
        <button
          onClick={() => setActiveView("sim")}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activeView === "sim" 
              ? "bg-purple-500/30 text-purple-300 shadow-lg shadow-purple-500/20" 
              : "text-white/50 hover:text-white/70 hover:bg-white/5"
          }`}
          data-testid="memory-toggle-simulated"
        >
          <Sparkles className="w-4 h-4" />
          Simulated
          {simFilledCount > 0 && (
            <span className="text-xs bg-purple-500/30 px-2 py-0.5 rounded-full">
              {simFilledCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveView("real")}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activeView === "real" 
              ? "bg-green-500/30 text-green-300 shadow-lg shadow-green-500/20" 
              : "text-white/50 hover:text-white/70 hover:bg-white/5"
          }`}
          data-testid="memory-toggle-real"
        >
          <CheckCircle2 className="w-4 h-4" />
          Real
          {realFilledCount > 0 && (
            <span className="text-xs bg-green-500/30 px-2 py-0.5 rounded-full">
              {realFilledCount}
            </span>
          )}
        </button>
      </div>
      
      {/* Header Card with Progress */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-purple-400" />
              <div>
                <CardTitle className="text-white">
                  What {matchName || "They"} Know{matchName ? "s" : ""} About You
                </CardTitle>
                <p className="text-white/50 text-sm mt-1">
                  {activeView === "sim" ? "Simulated" : "Real"} memory • {activeView === "sim" ? simFilledCount : realFilledCount} of {totalFields} fields
                </p>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center gap-2">
              {viewMode === "manage" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode("summary")}
                  className="border-white/20 text-white/70"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back to Summary
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-white/20 text-white/70">
                    <Settings className="w-4 h-4 mr-2" />
                    Presets
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-white/10">
                  <DropdownMenuItem 
                    onClick={() => handleApplyPreset("remember_all")}
                    className="text-white/80 focus:bg-white/10"
                  >
                    <Shield className="w-4 h-4 mr-2 text-green-400" />
                    Remember Everything
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleApplyPreset("private_mode")}
                    className="text-white/80 focus:bg-white/10"
                  >
                    <ShieldOff className="w-4 h-4 mr-2 text-amber-400" />
                    Private Mode
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshMemory}
                className="text-white/50 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Legend with clickable icons for help modal */}
          <div className="flex items-center gap-4 mt-4 text-white/40 text-xs">
            <button
              onClick={() => setShowPrivacyHelp(true)}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 cursor-pointer"
            >
              <Brain className="w-3.5 h-3.5 text-purple-400" />
              <span>Record</span>
            </button>
            
            <button
              onClick={() => setShowPrivacyHelp(true)}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 cursor-pointer"
            >
              <Eye className="w-3.5 h-3.5 text-blue-400" />
              <span>Use in Chat</span>
            </button>
            
            <div className="ml-auto text-white/50">
              {activeView === "sim" ? "Simulated" : "Real"}: {activeView === "sim" ? simFilledCount : realFilledCount} / {totalFields} fields
            </div>
          </div>
        </CardHeader>
      </Card>
      
      {/* Privacy Controls Help Modal */}
      <Dialog open={showPrivacyHelp} onOpenChange={setShowPrivacyHelp}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-divine-gold" />
              Memory Privacy Controls
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Brain / Record */}
            <div className="flex gap-4">
              <div className="p-3 rounded-lg bg-purple-500/20 h-fit">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h4 className="font-medium text-purple-300 mb-1">Record Toggle</h4>
                <p className="text-white/70 text-sm">
                  When <span className="text-green-400">ON</span>, the system captures and updates this memory field from your conversations.
                </p>
                <p className="text-white/50 text-xs mt-2">
                  Turn <span className="text-red-400">OFF</span> to stop learning new values - useful for fields you've manually set and don't want overwritten.
                </p>
              </div>
            </div>
            
            {/* Eye / Use in Chat */}
            <div className="flex gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20 h-fit">
                <Eye className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h4 className="font-medium text-blue-300 mb-1">Use in Chat Toggle</h4>
                <p className="text-white/70 text-sm">
                  When <span className="text-green-400">ON</span>, this field's value is included in the persona's prompt - they "know" this information.
                </p>
                <p className="text-white/50 text-xs mt-2">
                  Turn <span className="text-red-400">OFF</span> to hide from persona - useful for private notes or sensitive info you don't want referenced in chat.
                </p>
              </div>
            </div>
            
            {/* Common combinations */}
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-white/80 text-sm font-medium mb-3">Common Configurations</h4>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <Brain className="w-3.5 h-3.5 text-purple-400" />
                  <Eye className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-white/60">Both ON - Full learning and usage (default)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Brain className="w-3.5 h-3.5 text-white/30" />
                  <Eye className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-white/60">Record OFF, Use ON - Keep current value, stop updating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Brain className="w-3.5 h-3.5 text-purple-400" />
                  <Eye className="w-3.5 h-3.5 text-white/30" />
                  <span className="text-white/60">Record ON, Use OFF - Private notes (persona can't see)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Brain className="w-3.5 h-3.5 text-white/30" />
                  <Eye className="w-3.5 h-3.5 text-white/30" />
                  <span className="text-white/60">Both OFF - Completely private, no learning or usage</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPrivacyHelp(false)} className="bg-divine-gold text-black hover:bg-divine-gold/80">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Summary View */}
      {viewMode === "summary" && (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-white/50" />
                <CardTitle className="text-white text-lg">Recent Memories</CardTitle>
                {totalClaims > 0 && (
                  <Badge variant="secondary" className="bg-white/10 text-white/60">
                    {totalClaims} total
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode("manage")}
                className="border-white/20 text-white/70"
              >
                <ListFilter className="w-4 h-4 mr-2" />
                Manage All Memories
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {claimsLoading && recentClaims.length === 0 ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 text-white/50 animate-spin" />
              </div>
            ) : recentClaims.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <Brain className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No memories recorded yet</p>
                <p className="text-sm mt-2">Start chatting to build relationship memory</p>
              </div>
            ) : (
              <div className="space-y-1">
                {/* Dedupe by field_key and filter out "not set" fields */}
                {(() => {
                  const seen = new Set();
                  return recentClaims
                    .filter(claim => {
                      // Skip duplicates
                      if (seen.has(claim.field_key)) return false;
                      seen.add(claim.field_key);
                      // Filter out "not set" fields in Summary view
                      const currentValue = memory.sim_view?.[claim.field_key] || memory.real_view?.[claim.field_key];
                      return currentValue !== null && currentValue !== undefined && currentValue !== "";
                    })
                    .map((claim, idx) => {
                      const spec = specs.find(s => s.field_key === claim.field_key);
                      const currentValue = memory.sim_view[claim.field_key] || memory.real_view[claim.field_key];
                      
                      return (
                        <MemoryRow
                          key={`${claim.field_key}-${idx}`}
                          fieldKey={claim.field_key}
                          value={currentValue}
                          spec={spec}
                          source={claim.source_mode}
                          timestamp={claim.created_at}
                          onEdit={() => handleStartEditSummary(claim.field_key, currentValue)}
                          onClear={() => handleClear(claim.field_key)}
                          onViewHistory={() => setEvidenceField(claim.field_key)}
                          isEditing={editingField === claim.field_key}
                          editValue={editValue}
                          setEditValue={setEditValue}
                          onSaveEdit={handleSaveEditSummary}
                          onCancelEdit={handleCancelEditSummary}
                          showFieldNames={showFieldNames}
                          userTimezone={userTimezone}
                          compact
                        />
                      );
                    });
                })()}
                
                {/* Load more button */}
                {hasMoreClaims && (
                  <div className="pt-4 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadMoreClaims}
                      disabled={claimsLoading}
                      className="border-white/20 text-white/70"
                    >
                      {claimsLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <MoreHorizontal className="w-4 h-4 mr-2" />
                      )}
                      Load More ({totalClaims - recentClaims.length} remaining)
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Manage View (Full categorized list) */}
      {viewMode === "manage" && (
        <div className="w-full">
          {/* Profile Sharing Master Toggle + Info buttons */}
          <div className="flex items-center justify-between gap-3 mb-4">
            {/* Profile Sharing Toggle */}
            {userBaseData.has_user_persona && (
              <div className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 text-sm">👤</span>
                  <span className="text-white/80 text-sm">
                    Share {userBaseData.user_name}&apos;s profile
                  </span>
                </div>
                <Switch
                  checked={activeView === "sim" ? userBaseData.sim_master_enabled : userBaseData.live_master_enabled}
                  onCheckedChange={handleProfileSharingMasterToggle}
                  data-testid="profile-sharing-master-toggle"
                />
                <Badge 
                  variant="outline" 
                  className={`text-[10px] ${
                    (activeView === "sim" ? userBaseData.sim_master_enabled : userBaseData.live_master_enabled)
                      ? "border-amber-500/30 text-amber-400" 
                      : "border-white/20 text-white/40"
                  }`}
                >
                  {(activeView === "sim" ? userBaseData.sim_master_enabled : userBaseData.live_master_enabled) ? "ON" : "OFF"}
                </Badge>
              </div>
            )}
            
            {/* Info buttons */}
            <div className="flex items-center gap-3 text-white/50 text-xs ml-auto">
              <button
                onClick={() => setShowPrivacyHelp(true)}
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
              >
                <Brain className="w-3.5 h-3.5 text-purple-400" />
                <span>Record</span>
              </button>
              <button
                onClick={() => setShowPrivacyHelp(true)}
                className="flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
              >
                <Eye className="w-3.5 h-3.5 text-blue-400" />
                <span>Use in Chat</span>
              </button>
            </div>
          </div>
          
          {/* Categories list - uses activeView from main toggle */}
          <div className="space-y-2">
            {orderedCategories.map(categoryKey => (
              <CategorySection
                key={categoryKey}
                categoryKey={categoryKey}
                fields={groupedFields[categoryKey] || []}
                specs={specs}
                simView={memory.sim_view}
                realView={memory.real_view}
                controls={memory.controls}
                onUpdate={handleUpdate}
                onClear={handleClear}
                onToggleControl={handleToggleControl}
                onViewClaims={setEvidenceField}
                activeView={activeView}
                showFieldNames={showFieldNames}
                userBaseDataValues={userBaseData.field_values}
                isUserDataFieldEnabled={isUserBaseDataFieldEnabled}
                onToggleUserData={handleProfileSharingFieldToggle}
                showUserData={(activeView === "sim" ? userBaseData.sim_master_enabled : userBaseData.live_master_enabled) && userBaseData.has_user_persona}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Claims Editor Sheet */}
      <ClaimsEditorSheet
        matchId={matchId}
        fieldKey={evidenceField}
        isOpen={!!evidenceField}
        onClose={() => setEvidenceField(null)}
        onClaimsChanged={refreshMemory}
      />
    </div>
  );
}
